import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PortalEventContextType, type PortalSourcingEventDetailDto } from "../../autogen/bff-api";
import { useRunsPublicProcurements } from "../organization/useRunsPublicProcurements";

// i18next-parser only extracts literal t() calls, so keys built dynamically get pruned from locales.
const termPairs = (t: TFunction) =>
  ({
    awardCriteria: [t("Award criteria"), t("Evaluation requirements")],
    awardCriteriaModel: [t("Award criteria model"), t("Evaluation model")],
    awardModel: [t("Award model"), t("Evaluation model")],
    awardMethod: [t("Award method"), t("Evaluation method")],
    awardSubCriteria: [t("Award requirements"), t("Evaluation sub-requirements")],
    awardCriteriaNoun: [t("award criteria"), t("evaluation requirements")],
    addAwardCriteria: [t("Add award criteria"), t("Add evaluation requirements")],
    addCriterion: [t("Add criterion"), t("Add requirement")],
    newCriterion: [t("New criterion"), t("New requirement")],
    deleteCriterion: [t("Delete criterion"), t("Delete requirement")],
    deleteCriterionConfirm: [
      t("Are you sure you want to delete this criterion?"),
      t("Are you sure you want to delete this requirement?"),
    ],
    criteriaLockedAfterPublication: [
      t("Criteria are locked after publication"),
      t("Requirements are locked after publication"),
    ],
    noCriteriaAddedYet: [t("No criteria added yet"), t("No requirements added yet")],
    scoreBidsAgainstCriteria: [
      t("Score the bids against weighted criteria instead of price alone."),
      t("Score the bids against weighted requirements instead of price alone."),
    ],
    weightedAwardCriteriaHint: [
      t("Weighted award criteria that together make up 100 %."),
      t("Weighted evaluation requirements that together make up 100 %."),
    ],
    lowestPriceHasNoAwardCriteria: [
      t("Award is based on the lowest price. No award criteria to weight."),
      t("Award is based on the lowest price. No evaluation requirements to weight."),
    ],
    replacesCurrentAwardCriteria: [
      t("Replaces the current award criteria:"),
      t("Replaces the current evaluation requirements:"),
    ],
    yourAwardCriteria: [t("Your award criteria"), t("Your evaluation requirements")],
    noOwnAwardCriteria: [t("No award criteria of your own yet."), t("No evaluation requirements of your own yet.")],
    noStandardAwardCriteria: [
      t("No SourceMagnet standard award criteria yet."),
      t("No SourceMagnet standard evaluation requirements yet."),
    ],
    addReusableAwardCriteria: [
      t("Add reusable award criteria your organization can apply."),
      t("Add reusable evaluation requirements your organization can apply."),
    ],
    chooseAwardModelHint: [
      t("Choose an award model. For best price/quality, add weighted criteria that sum to 100 %."),
      t("Choose an evaluation model. For best price/quality, add weighted requirements that sum to 100 %."),
    ],
    noAwardModelChosenHint: [
      t("No award model chosen. Applying the template leaves the procurement's award model untouched."),
      t("No evaluation model chosen. Applying the template leaves the procurement's evaluation model untouched."),
    ],
    clearAwardModel: [t("Clear award model"), t("Clear evaluation model")],
    noAwardModelChosenYet: [t("No award model chosen yet"), t("No evaluation model chosen yet")],
    removeAwardCriteriaTitle: [t("Remove the award criteria?"), t("Remove the evaluation requirements?")],
    removeAwardCriteriaBody: [
      t("The award model and its criteria are removed from this procurement."),
      t("The evaluation model and its requirements are removed from this procurement."),
    ],
    scorePerAwardCriterion: [t("Score per award criterion"), t("Score per evaluation requirement")],
    pointsAreGivenOnEachBid: [
      t("Open a bid to score it against the award criteria."),
      t("Open a bid to score it against the evaluation requirements."),
    ],
    simplifiedRegime: [t("Del I procurement"), t("Private procurement")],
    newSimplifiedDraft: [t("New Del I procurement"), t("New sourcing event")],
    changeToSimplified: [t("Change to a Del I procurement"), t("Change to a private procurement")],
    changeToSimplifiedQuestion: [t("Change to a Del I procurement?"), t("Change to a private procurement?")],
    changeToSimplifiedShort: [t("Change to Del I"), t("Change to private")],
    publishedStatus: [t("Notice published"), t("Published")],
    newProcurement: [t("New procurement"), t("New sourcing event")],
    noProcurementsMatchFilters: [
      t("No procurements match your search or filters."),
      t("No sourcing events match your search or filters."),
    ],
    cannotCreateProcurementInAnyDepartment: [
      t("You cannot create procurements in any department in this organization."),
      t("You cannot create sourcing events in any department in this organization."),
    ],
    whichDepartmentOwnsProcurement: [
      t("Which department should the procurement belong to?"),
      t("Which department should the sourcing event belong to?"),
    ],
    templateReuseHint: [
      t("Create a template to reuse across procurements."),
      t("Create a template to reuse across sourcing events."),
    ],
    assistantOverviewCapability: [
      t("Get an overview of your procurements, deadlines and status."),
      t("Get an overview of your sourcing events, deadlines and status."),
    ],
    assistantFindCapability: [
      t("Find and open a specific procurement or contract."),
      t("Find and open a specific sourcing event or contract."),
    ],
    assistantOpenProcurementsPrompt: [t("Show my open procurements"), t("Show my open sourcing events")],
    assistantBidCountPrompt: [
      t("How many bids are on the most recent procurement?"),
      t("How many bids are on the most recent sourcing event?"),
    ],
    assistantHelpHeading: [
      t("How can I help with your procurements?"),
      t("How can I help with your sourcing events?"),
    ],
    assistantScopeIntro: [
      t(
        "I work across your procurements, contracts and tender documents — ask a question, search, draft a text, or let me take you straight to the right page.",
      ),
      t(
        "I work across your sourcing events, contracts and tender documents — ask a question, search, draft a text, or let me take you straight to the right page.",
      ),
    ],
    assistantAskPlaceholder: [
      t("Ask about your procurements, deadlines, bids or documents."),
      t("Ask about your sourcing events, deadlines, bids or documents."),
    ],
  }) as const satisfies Record<string, readonly [string, string]>;

const parameterizedTerms = (t: TFunction, buyerRunsPublicProcurements: boolean) => ({
  valueAboveSimplifiedThreshold: (threshold: string) =>
    buyerRunsPublicProcurements
      ? t(
          "The estimated value is at or above {{threshold}} NOK, but this procurement is already registered as a Del I procurement",
          { threshold },
        )
      : t(
          "The estimated value is at or above {{threshold}} NOK, but this procurement is already registered as private",
          { threshold },
        ),
  awardCriteriaCount: (count: number) =>
    buyerRunsPublicProcurements
      ? t("{{count}} award criteria", { count })
      : t("{{count}} evaluation requirements", { count }),
  selectedCriteriaWeightTotal: (total: number, step: number) =>
    buyerRunsPublicProcurements
      ? t("The selected criteria add up to {{total}} %. You can adjust the weights in step {{step}}.", { total, step })
      : t("The selected requirements add up to {{total}} %. You can adjust the weights in step {{step}}.", { total, step }),
  templateDropsWeightedCriteria: (count: number) =>
    buyerRunsPublicProcurements
      ? t("The template uses lowest price. The {{count}} weighted award criteria on this procurement are removed.", { count })
      : t("The template uses lowest price. The {{count}} weighted evaluation requirements on this procurement are removed.", { count }),
});

type PairedTerms = { [K in keyof ReturnType<typeof termPairs>]: string };
export type ProcurementTerms = PairedTerms & ReturnType<typeof parameterizedTerms>;

const useTerms = (buyerRunsPublicProcurements: boolean): ProcurementTerms => {
  const { t } = useTranslation();
  return useMemo(() => {
    const chosen = buyerRunsPublicProcurements ? 0 : 1;
    const paired = Object.fromEntries(
      Object.entries(termPairs(t)).map(([term, pair]) => [term, pair[chosen]]),
    ) as PairedTerms;
    return { ...paired, ...parameterizedTerms(t, buyerRunsPublicProcurements) };
  }, [t, buyerRunsPublicProcurements]);
};

export const useProcurementTerms = (): ProcurementTerms => useTerms(useRunsPublicProcurements());

export const usePortalProcurementTerms = (event: PortalSourcingEventDetailDto): ProcurementTerms =>
  useTerms(
    event.contextType === PortalEventContextType.Public || (event.buyerRunsPublicProcurements ?? false),
  );
